// ---------------------------------------------------------------------------
// Employee migration — shared field config & validation.
//
// The backend round-trips rows keyed in PascalCase (see
// EmployeeMigrationDetails.employee_value() and change_keys() in the BE), so
// the FE keeps the same keys end-to-end: CSV header -> grid row -> process
// payload. This file is the single source of truth for the columns, their
// option sources, and per-field validation so the grid and the upload parser
// never drift apart.
// ---------------------------------------------------------------------------
import dayjs from "dayjs";

// Static option sets — kept in sync with the values the employee module uses
// (titleEnum/genderEnum/relationshipEnum/bloodGroupEnum in UserCreation). Inlined
// here so the migration bundle doesn't pull in a whole page component.
export const titleEnum = [
    { value: "Mr", text: "Mr" },
    { value: "Ms", text: "Ms" },
    { value: "Mrs", text: "Mrs" },
    { value: "Dr", text: "Dr" },
];

export const genderEnum = [
    { value: "male", text: "Male" },
    { value: "female", text: "Female" },
    { value: "others", text: "Others" },
];

export const relationshipEnum = [
    { value: "mother", text: "Mother" },
    { value: "father", text: "Father" },
    { value: "spouse", text: "Spouse" },
    { value: "husband", text: "Husband" },
    { value: "brother", text: "Brother" },
    { value: "sister", text: "Sister" },
    { value: "friend", text: "Friend" },
    { value: "others", text: "Others" },
];

export const bloodGroupEnum = [
    { value: "O-Ve", text: "O-Ve" }, { value: "O+Ve", text: "O+Ve" },
    { value: "A+Ve", text: "A+Ve" }, { value: "A-Ve", text: "A-Ve" },
    { value: "A1+Ve", text: "A1+Ve" }, { value: "A1-Ve", text: "A1-Ve" },
    { value: "A1B+Ve", text: "A1B+Ve" }, { value: "A1B-Ve", text: "A1B-Ve" },
    { value: "A2+Ve", text: "A2+Ve" }, { value: "A2-Ve", text: "A2-Ve" },
    { value: "A2B+Ve", text: "A2B+Ve" }, { value: "A2B-Ve", text: "A2B-Ve" },
    { value: "AB+Ve", text: "AB+Ve" }, { value: "AB-Ve", text: "AB-Ve" },
    { value: "B+Ve", text: "B+Ve" }, { value: "B-Ve", text: "B-Ve" },
];

// Master dropdowns are fetched once by the wizard and passed down by this key.
// `label` is the property on each option holding the display text the CSV/grid
// stores (we match by display name, mirroring the BE name->id resolution).
export const OPTION_SOURCES = {
    department: { label: "department_name" },
    designation: { label: "designation_name" },
    emptype: { label: "employee_type_name" },
    shift: { label: "shift_name" },
    usergroup: { label: "user_group_name" },
    empgroup: { label: "employee_group_name" },
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const TEN_DIGITS_RE = /^\d{10}$/;
const SIX_DIGITS_RE = /^\d{6}$/;
const ISO_DATE_RE = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

const trimmed = (v) => (v === null || v === undefined ? "" : String(v).trim());

// --- reusable field validators (return an error string or null) -------------
const text = (max = 50) => (v) => {
    const s = trimmed(v);
    if (!s) return "This field is required";
    if (s.length > max) return `Must be ${max} characters or fewer`;
    return null;
};
const optionalText = (max = 50) => (v) => {
    const s = trimmed(v);
    return s.length > max ? `Must be ${max} characters or fewer` : null;
};
const email = (v) => (EMAIL_RE.test(trimmed(v)) ? null : "Enter a valid email address");
const mobile = (v) => (TEN_DIGITS_RE.test(trimmed(v)) ? null : "Enter a 10-digit number");
const pincode = (v) => (SIX_DIGITS_RE.test(trimmed(v)) ? null : "Enter a 6-digit pincode");
const isoDate = (v) => (ISO_DATE_RE.test(trimmed(v)) ? null : "Use date format YYYY-MM-DD");

// Password rules come from the org password policy cached in sessionStorage.
// Falls back to "non-empty" when the policy isn't available.
export const validatePassword = (value) => {
    const v = value ?? "";
    if (!v) return "Password is required";
    let policy = {};
    try {
        policy = JSON.parse(sessionStorage.getItem("passwordPolicy")) || {};
    } catch {
        policy = {};
    }
    if (!Object.keys(policy).length) return null;

    const within = (lo, n, hi) =>
        (lo === undefined || n >= lo) && (hi === undefined || n <= hi);

    if (!within(policy.min_length, v.length, policy.max_length)) {
        return `Password must be ${policy.min_length}-${policy.max_length} characters`;
    }
    const nums = (v.match(/[0-9]/g) || []).length;
    if (!within(policy.min_number, nums, policy.max_number)) {
        return `Password needs ${policy.min_number}-${policy.max_number} digits`;
    }
    const lower = (v.match(/[a-z]/g) || []).length;
    if (!within(policy.min_lower, lower, policy.max_lower)) {
        return `Password needs ${policy.min_lower}-${policy.max_lower} lowercase letters`;
    }
    const upper = (v.match(/[A-Z]/g) || []).length;
    if (!within(policy.min_upper, upper, policy.max_upper)) {
        return `Password needs ${policy.min_upper}-${policy.max_upper} uppercase letters`;
    }
    if (!policy.password_combination && /[^A-Za-z0-9]/.test(v)) {
        return "Password must not contain special characters";
    }
    return null;
};

const inEnum = (options) => (v) =>
    options.some((o) => o.text === trimmed(v)) ? null : "Select a valid option";

const inOptions = (sourceKey) => (v, _row, opts = {}) => {
    const list = opts[sourceKey] || [];
    const label = OPTION_SOURCES[sourceKey].label;
    return list.some((o) => o[label] === trimmed(v)) ? null : "Select a valid option";
};

// --- field definitions (order = column / CSV order) -------------------------
// kind: "text" | "select" (master) | "enum" (static) | "date"
export const FIELDS = [
    { key: "Title", label: "Title", required: true, kind: "enum", enumName: "title", enum: titleEnum, validate: inEnum(titleEnum) },
    { key: "FirstName", label: "First Name", required: true, kind: "text", validate: text(50) },
    { key: "LastName", label: "Last Name", required: true, kind: "text", validate: text(50) },
    { key: "PersonalEmailID", label: "Personal Email", required: true, kind: "text", validate: email },
    { key: "PersonalMobileNo", label: "Personal Mobile", required: true, kind: "text", validate: mobile },
    { key: "ProfessionalEmailID", label: "Professional Email", required: true, kind: "text", validate: email },
    { key: "Department", label: "Department", required: true, kind: "select", source: "department", validate: inOptions("department") },
    { key: "Designation", label: "Designation", required: true, kind: "select", source: "designation", validate: inOptions("designation") },
    { key: "EmployeeType", label: "Employee Type", required: true, kind: "select", source: "emptype", validate: inOptions("emptype") },
    { key: "DateOfJoining", label: "Date of Joining", required: true, kind: "date", validate: isoDate },
    { key: "Shift", label: "Shift", required: true, kind: "select", source: "shift", validate: inOptions("shift") },
    { key: "City", label: "City", required: true, kind: "text", validate: text(50) },
    { key: "State", label: "State", required: true, kind: "text", validate: text(50) },
    { key: "Pincode", label: "Pincode", required: true, kind: "text", validate: pincode },
    { key: "PresentAddressCity", label: "Present City", required: true, kind: "text", validate: text(50) },
    { key: "PresentAddressState", label: "Present State", required: true, kind: "text", validate: text(50) },
    { key: "PresentAddressPincode", label: "Present Pincode", required: true, kind: "text", validate: pincode },
    { key: "Usergroup", label: "User Group", required: true, kind: "select", source: "usergroup", validate: inOptions("usergroup") },
    { key: "Username", label: "Username", required: true, kind: "text", validate: text(50) },
    { key: "Password", label: "Password", required: true, kind: "password", validate: validatePassword },
    { key: "DisplayName", label: "Display Name", required: false, kind: "text", validate: optionalText(50) },
    { key: "Gender", label: "Gender", required: true, kind: "enum", enumName: "gender", enum: genderEnum, validate: inEnum(genderEnum) },
    { key: "Address", label: "Address", required: false, kind: "text", validate: optionalText(200) },
    { key: "ContactPersonName", label: "Contact Person", required: true, kind: "text", validate: text(50) },
    { key: "RelationshipType", label: "Relationship", required: true, kind: "enum", enumName: "relationship", enum: relationshipEnum, validate: inEnum(relationshipEnum) },
    { key: "ContactPersonMobileNo", label: "Contact Mobile", required: true, kind: "text", validate: mobile },
    { key: "EmployeeGroup", label: "Employee Group", required: true, kind: "select", source: "empgroup", validate: inOptions("empgroup") },
    { key: "BloodGroup", label: "Blood Group", required: true, kind: "enum", enumName: "blood", enum: bloodGroupEnum, validate: inEnum(bloodGroupEnum) },
    { key: "EmergencyContactPerson", label: "Emergency Contact", required: true, kind: "text", validate: text(50) },
    { key: "EmergencyContactNo", label: "Emergency Mobile", required: true, kind: "text", validate: mobile },
    { key: "EmergencyRelationshipType", label: "Emergency Relationship", required: true, kind: "enum", enumName: "relationship", enum: relationshipEnum, validate: inEnum(relationshipEnum) },
    { key: "PresentAddress", label: "Present Address", required: false, kind: "text", validate: optionalText(200) },
];

// CSV template headers (asterisk marks required) — must match the BE template
// column order so the uploaded sheet maps cleanly.
export const REQUIRED_HEADER = `${FIELDS[0].label} *`;

// Normalise a CSV header cell ("First Name *") to a row key ("FirstName").
export const headerToKey = (header) =>
    String(header || "").replace(/\*/g, "").replace(/\s+/g, "");

// Validate one row against the field rules + any server-side conflict comments.
// Returns a map of { fieldKey: errorMessage }. Empty map === row is clean.
export const validateRow = (row, options = {}) => {
    const errors = {};
    for (const f of FIELDS) {
        const msg = f.validate?.(row?.[f.key], row, options);
        if (msg) errors[f.key] = msg;
    }
    // Server conflicts (uniqueness etc.) arrive in `comments` keyed by BE field.
    if (Array.isArray(row?.comments)) {
        for (const c of row.comments) {
            const key = SNAKE_TO_PASCAL[c.key];
            // Only surface the server message while the value is unchanged.
            if (key && row[key] === c.value && !errors[key]) errors[key] = c.message;
        }
    }
    return errors;
};

export const rowHasErrors = (row, options) => Object.keys(validateRow(row, options)).length > 0;

// BE conflict comments are keyed in snake_case; map them back to row keys.
const SNAKE_TO_PASCAL = {
    personal_email_id: "PersonalEmailID",
    professional_email_id: "ProfessionalEmailID",
    personal_mobile_no: "PersonalMobileNo",
    username: "Username",
    title: "Title",
    first_name: "FirstName",
    last_name: "LastName",
    department: "Department",
    designation: "Designation",
    employee_type: "EmployeeType",
    shift: "Shift",
    employee_group: "EmployeeGroup",
    usergroup: "Usergroup",
    password: "Password",
};

// Attach a stable client key to each row so the editable grid can track rows by
// identity (not array index) — this is what lets cells memoize and skip
// re-rendering when an unrelated row changes. Server rows reuse their `id`.
let _rowKeySeq = 0;
export const ensureKeys = (rows = []) =>
    rows.map((r) => (r.__key != null ? r : { ...r, __key: r.id != null ? `id-${r.id}` : `n-${++_rowKeySeq}` }));

// dayjs helper kept here so the grid + upload parser format dates identically.
export const toIsoDate = (value) => {
    if (!value) return "";
    const s = String(value).trim();
    // Accept dd-mm-yyyy or dd/mm/yyyy and normalise to ISO.
    const dmY = /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/.exec(s);
    if (dmY) {
        const [, d, m, y] = dmY;
        const dt = dayjs(`${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`);
        return dt.isValid() ? dt.format("YYYY-MM-DD") : s;
    }
    const dt = dayjs(s);
    return dt.isValid() ? dt.format("YYYY-MM-DD") : s;
};
